/* eslint-disable @typescript-eslint/naming-convention */
import type React from 'react';
import { useEffect } from 'react';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { login as loginRoute } from 'src/app/onboarding/routes/routes';
import { useIdentity } from 'src/hooks/useIdentity';
import { useIsCheckedRoute } from 'src/hooks/useIsCheckedRoute';
import { shouldOptOutOfAmplitude } from 'src/lib/analytics/amplitude/helpers/shouldOptOutOfAmplitude';
import { useConsent } from 'src/lib/consent/useConsent';
import { isCypressUserEmail } from 'src/lib/isCypressUser';
import { useLDFlag } from 'src/lib/launchDarkly';
import { getAmplitudeClient, startAmplitude } from 'src/lib/tracking';

type amplitudeFlagConfig = {
  trackingEnabled: boolean;
  trackEmbeddedUsers: boolean;
};

type ReactChildren = {
  children: React.ReactElement;
};

const useAmplitudeConfig = (): amplitudeFlagConfig => {
  const trackingEnabled = useLDFlag('studio-amplitude-tracking-enabled');

  const trackEmbeddedUsers = useLDFlag('betelgeuse-amplitude-in-embedded');

  return {
    trackingEnabled,
    trackEmbeddedUsers,
  };
};

export const AmplitudeProvider = ({ children }: ReactChildren) => {
  const { me, isUser, myAccount, memberships } = useIdentity();
  const graphRef = useGraphRef();

  const amplitudeConfiguration = useAmplitudeConfig();
  const excludeRouteFromTracking =
    useIsCheckedRoute(loginRoute.definition) || isEmbeddableExplorerRoute();
  // logged in users always have consent, see useConsent
  const { performance: hasConsent } = useConsent();
  const shouldOptOut =
    !hasConsent ||
    shouldOptOutOfAmplitude({
      excludeRouteFromTracking,
      amplitudeConfiguration,
    });

  // Runs before the effects below, so they see the client the first time
  // Amplitude starts. Once started, opting out is all we can do.
  useEffect(() => {
    if (!shouldOptOut) startAmplitude();
    getAmplitudeClient()?.setOptOut(shouldOptOut);
  }, [shouldOptOut]);

  useEffect(() => {
    const amplitudeClient = getAmplitudeClient();
    // Identify when `me` or `isUser` is updated, or Amplitude starts
    if (amplitudeClient && me && isUser) {
      // Find the user's role for the current active account
      const userRole = memberships.find(
        (membership) => membership.account.id === myAccount?.id,
      )?.role;

      void amplitudeClient.identify(
        {
          'Active Org Id': myAccount?.internalID,
          active_org_subscription_tier: myAccount?.currentPlan?.tier,
          'Email Verified': me.emailVerified,
          Email: me?.email || undefined,
          'Full Name': me.fullName,
          user_role: userRole,
        },
        me.id,
      );
    }
  }, [
    shouldOptOut,
    me,
    isUser,
    graphRef,
    myAccount?.internalID,
    myAccount?.id,
    myAccount?.currentPlan?.tier,
    memberships,
  ]);

  // Remove Amplitude Session Replay plugin for Cypress test users after authentication
  useEffect(() => {
    if (me && isUser) {
      const userEmail = me.email;
      if (userEmail && isCypressUserEmail(userEmail)) {
        getAmplitudeClient()?.remove(
          '@amplitude/plugin-session-replay-browser',
        );
      }
    }
  }, [shouldOptOut, me, isUser]);

  return children;
};
